// Photo editor before sending (like WhatsApp's): crop (free or a fixed
// aspect), rotate, draw, add text, undo. Works on a canvas; the result
// replaces the photo in the send dialog.
import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "./Icon";

type Mode = "draw" | "text" | "crop";
type Rect = { x: number; y: number; w: number; h: number };

const COLORS = ["#ffffff", "#111111", "#ff3b30", "#ffcc00", "#34c759", "#0a84ff", "#af52de"];
const ASPECTS: { label: string; r: number | null }[] = [
  { label: "Free", r: null },
  { label: "Square", r: 1 },
  { label: "4:3", r: 4 / 3 },
  { label: "16:9", r: 16 / 9 },
];

function copy(src: HTMLCanvasElement) {
  const c = document.createElement("canvas");
  c.width = src.width;
  c.height = src.height;
  c.getContext("2d")!.drawImage(src, 0, 0);
  return c;
}

export function ImageEditor({ file, onDone, onCancel }: { file: File; onDone: (f: File) => void; onCancel: () => void }) {
  const view = useRef<HTMLCanvasElement>(null);
  const [history, setHistory] = useState<HTMLCanvasElement[]>([]);
  const base = history[history.length - 1];
  const [mode, setMode] = useState<Mode>("draw");
  const [color, setColor] = useState(COLORS[2]);
  const [size, setSize] = useState(6);
  const [crop, setCrop] = useState<Rect | null>(null);
  const [aspect, setAspect] = useState<number | null>(null);
  const [text, setText] = useState<{ x: number; y: number; value: string } | null>(null);
  const drawing = useRef<{ last: [number, number] } | null>(null);
  const drag = useRef<{ kind: "move" | "nw" | "ne" | "sw" | "se"; start: [number, number]; rect: Rect } | null>(null);
  const [, redraw] = useState(0);

  // Load the photo.
  useEffect(() => {
    void createImageBitmap(file).then((bmp) => {
      const c = document.createElement("canvas");
      c.width = bmp.width;
      c.height = bmp.height;
      c.getContext("2d")!.drawImage(bmp, 0, 0);
      bmp.close();
      setHistory([c]);
    });
  }, [file]);

  // Show the current image.
  useEffect(() => {
    const v = view.current;
    if (!v || !base) return;
    v.width = base.width;
    v.height = base.height;
    v.getContext("2d")!.drawImage(base, 0, 0);
  }, [base]);

  const commit = (c: HTMLCanvasElement) => setHistory((h) => [...h.slice(-19), c]);
  /** Client coordinates -> image pixels. */
  const toImage = (ev: { clientX: number; clientY: number }): [number, number] => {
    const r = view.current!.getBoundingClientRect();
    return [((ev.clientX - r.left) / r.width) * base!.width, ((ev.clientY - r.top) / r.height) * base!.height];
  };
  const scale = () => (base ? base.width / view.current!.getBoundingClientRect().width : 1);

  // ---- draw
  function down(ev: PointerEvent) {
    if (!base) return;
    if (mode === "text") {
      const [x, y] = toImage(ev);
      setText({ x, y, value: "" });
      return;
    }
    if (mode !== "draw") return;
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    drawing.current = { last: toImage(ev) };
  }
  function move(ev: PointerEvent) {
    if (!drawing.current || mode !== "draw") return;
    const p = toImage(ev);
    const g = view.current!.getContext("2d")!;
    g.strokeStyle = color;
    g.lineWidth = size * scale();
    g.lineCap = g.lineJoin = "round";
    g.beginPath();
    g.moveTo(...drawing.current.last);
    g.lineTo(...p);
    g.stroke();
    drawing.current.last = p;
  }
  function up() {
    if (!drawing.current) return;
    drawing.current = null;
    commit(copy(view.current!)); // the stroke becomes part of the image
  }

  // ---- text
  function placeText() {
    if (!text || !text.value.trim() || !base) return setText(null);
    const c = copy(base);
    const g = c.getContext("2d")!;
    const px = Math.round(28 * scale() * (size / 6));
    g.font = `700 ${px}px system-ui, sans-serif`;
    g.textBaseline = "middle";
    g.lineWidth = Math.max(2, px / 8);
    g.strokeStyle = color === "#111111" ? "#ffffff" : "#000000";
    g.fillStyle = color;
    const w = g.measureText(text.value).width;
    const x = Math.min(Math.max(0, text.x - w / 2), c.width - w);
    g.strokeText(text.value, x, text.y);
    g.fillText(text.value, x, text.y);
    commit(c);
    setText(null);
  }

  // ---- crop (set up right away, not in an effect: a quick "Square" right
  // after "Crop" must not be overwritten by a late full-size box)
  function toggleCrop() {
    if (mode === "crop") return (setMode("draw"), setCrop(null));
    setMode("crop");
    setAspect(null);
    if (base) setCrop({ x: 0, y: 0, w: base.width, h: base.height });
  }
  const leaveCrop = (next: Mode) => (setMode(next), setCrop(null));
  function setAspectRatio(r: number | null) {
    setAspect(r);
    if (!r || !base) return;
    // Largest centred rectangle of that shape.
    let w = base.width;
    let h = w / r;
    if (h > base.height) (h = base.height), (w = h * r);
    setCrop({ x: (base.width - w) / 2, y: (base.height - h) / 2, w, h });
  }
  function cropDown(ev: PointerEvent, kind: "move" | "nw" | "ne" | "sw" | "se") {
    ev.stopPropagation();
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    drag.current = { kind, start: toImage(ev), rect: { ...crop! } };
  }
  function cropMove(ev: PointerEvent) {
    const d = drag.current;
    if (!d || !base) return;
    const [x, y] = toImage(ev);
    const dx = x - d.start[0];
    const dy = y - d.start[1];
    const r = { ...d.rect };
    const min = 40;
    if (d.kind === "move") {
      r.x = Math.min(Math.max(0, d.rect.x + dx), base.width - r.w);
      r.y = Math.min(Math.max(0, d.rect.y + dy), base.height - r.h);
    } else {
      const left = d.kind === "nw" || d.kind === "sw";
      const top = d.kind === "nw" || d.kind === "ne";
      let x0 = left ? d.rect.x + dx : d.rect.x;
      let x1 = left ? d.rect.x + d.rect.w : d.rect.x + d.rect.w + dx;
      let y0 = top ? d.rect.y + dy : d.rect.y;
      let y1 = top ? d.rect.y + d.rect.h : d.rect.y + d.rect.h + dy;
      x0 = Math.max(0, Math.min(x0, x1 - min));
      x1 = Math.min(base.width, Math.max(x1, x0 + min));
      y0 = Math.max(0, Math.min(y0, y1 - min));
      y1 = Math.min(base.height, Math.max(y1, y0 + min));
      r.x = x0;
      r.y = y0;
      r.w = x1 - x0;
      r.h = y1 - y0;
      if (aspect) {
        r.h = r.w / aspect;
        if (top) r.y = y1 - r.h;
        if (r.y < 0 || r.y + r.h > base.height) return; // keep the shape inside
      }
    }
    setCrop(r);
  }
  function applyCrop() {
    if (!crop || !base) return;
    const c = document.createElement("canvas");
    c.width = Math.round(crop.w);
    c.height = Math.round(crop.h);
    c.getContext("2d")!.drawImage(base, crop.x, crop.y, crop.w, crop.h, 0, 0, c.width, c.height);
    commit(c);
    leaveCrop("draw");
  }
  function rotate() {
    if (!base) return;
    const c = document.createElement("canvas");
    c.width = base.height;
    c.height = base.width;
    const g = c.getContext("2d")!;
    g.translate(c.width, 0);
    g.rotate(Math.PI / 2);
    g.drawImage(base, 0, 0);
    commit(c);
    if (mode === "crop") (setAspect(null), setCrop({ x: 0, y: 0, w: c.width, h: c.height }));
  }

  async function done() {
    if (!base) return;
    const png = file.type === "image/png";
    const blob = await new Promise<Blob>((res) => base.toBlob((b) => res(b!), png ? "image/png" : "image/jpeg", 0.92));
    onDone(new File([blob], file.name.replace(/\.\w+$/, "") + (png ? ".png" : ".jpg"), { type: blob.type }));
  }

  // The crop box, in screen coordinates over the canvas.
  const box = (() => {
    if (!crop || !view.current || !base) return null;
    const r = view.current.getBoundingClientRect();
    const host = view.current.parentElement!.getBoundingClientRect();
    const k = r.width / base.width;
    return { left: r.left - host.left + crop.x * k, top: r.top - host.top + crop.y * k, width: crop.w * k, height: crop.h * k };
  })();

  return (
    <div class="img-editor" role="dialog" aria-label="Edit photo">
      <header class="img-editor-bar">
        <button class="icon-btn light" onClick={onCancel} aria-label="Cancel editing">
          <Icon name="close" />
        </button>
        <div class="img-tools" role="toolbar">
          <button class={"icon-btn light" + (mode === "crop" ? " on" : "")} onClick={toggleCrop} aria-label="Crop" title="Crop">
            <Icon name="crop" />
          </button>
          <button class="icon-btn light" onClick={rotate} aria-label="Rotate" title="Rotate">
            <Icon name="refresh" />
          </button>
          <button class={"icon-btn light" + (mode === "text" ? " on" : "")} onClick={() => leaveCrop("text")} aria-label="Text" title="Text">
            <span class="tool-t">T</span>
          </button>
          <button class={"icon-btn light" + (mode === "draw" ? " on" : "")} onClick={() => leaveCrop("draw")} aria-label="Draw" title="Draw">
            <Icon name="edit" />
          </button>
          <button class="icon-btn light" disabled={history.length < 2} onClick={() => setHistory((h) => h.slice(0, -1))} aria-label="Undo" title="Undo">
            <Icon name="reply" />
          </button>
        </div>
        <button class="icon-btn send-fab" onClick={() => void done()} aria-label="Done editing" disabled={!base}>
          <Icon name="check" />
        </button>
      </header>
      <div class="img-stage" onPointerMove={(e) => (drag.current ? cropMove(e) : move(e))} onPointerUp={() => ((drag.current = null), up(), redraw((n) => n + 1))}>
        <canvas ref={view} class={"img-canvas " + mode} onPointerDown={down} aria-label="Photo" />
        {box && (
          <div class="crop-box" style={{ left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` }} onPointerDown={(e) => cropDown(e, "move")}>
            {(["nw", "ne", "sw", "se"] as const).map((k) => (
              <span key={k} class={"crop-handle " + k} onPointerDown={(e) => cropDown(e, k)} aria-label={`Crop corner ${k}`} />
            ))}
          </div>
        )}
        {text && view.current && base && (
          <input
            class="img-text-input"
            autoFocus
            ref={(el) => el?.focus()}
            value={text.value}
            style={{
              left: `${(text.x / base.width) * 100}%`,
              top: `${(text.y / base.height) * 100}%`,
              color,
            }}
            onInput={(e) => setText({ ...text, value: e.currentTarget.value })}
            onKeyDown={(e) => (e.key === "Enter" ? placeText() : e.key === "Escape" && setText(null))}
            // (The click that opened it also moves focus: an empty box stays.)
            onBlur={() => text.value.trim() && placeText()}
            aria-label="Text on photo"
          />
        )}
      </div>
      <footer class="img-editor-foot">
        {mode === "crop" ? (
          <>
            {ASPECTS.map((a) => (
              <button key={a.label} class={"chip" + (aspect === a.r ? " on" : "")} onClick={() => setAspectRatio(a.r)}>
                {a.label}
              </button>
            ))}
            <button class="primary" onClick={applyCrop} aria-label="Apply crop">
              Apply
            </button>
          </>
        ) : (
          <>
            {COLORS.map((c) => (
              <button key={c} class={"color-dot" + (c === color ? " on" : "")} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Colour ${c}`} />
            ))}
            <span class="sizes">
              {[3, 6, 12].map((s) => (
                <button key={s} class={"size-dot" + (s === size ? " on" : "")} onClick={() => setSize(s)} aria-label={`Size ${s}`}>
                  <span style={{ width: `${s + 4}px`, height: `${s + 4}px` }} />
                </button>
              ))}
            </span>
          </>
        )}
      </footer>
    </div>
  );
}
